import React, { useState, useEffect, useRef, useCallback } from 'react';
import { GoogleMap, Marker, Circle, Polygon } from '@react-google-maps/api';
import { getUbicacionesOnAsset, UbicacionOnAsset } from '../../apis/dispositivosApi';
import { obtenerGeocercas, Geocerca } from '../../apis/geocercasApi';
import { obtenerEventosGeocerca, EventoGeocerca } from '../../apis/ubicacionesApi';
import { MAP_CONFIG } from '../../config/googleMaps';
import { useGoogleMaps } from '../../context/GoogleMapsContext';

interface GoogleMapScreenWithGeocercaControlsProps {
  token: string;
}

const GoogleMapScreenWithGeocercaControls: React.FC<GoogleMapScreenWithGeocercaControlsProps> = ({ token }) => {
  const { isLoaded, loadError } = useGoogleMaps();
  const [ubicacionesOnAsset, setUbicacionesOnAsset] = useState<UbicacionOnAsset[]>([]);
  const [geocercas, setGeocercas] = useState<Geocerca[]>([]);
  const [eventos, setEventos] = useState<EventoGeocerca[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [isMapReady, setIsMapReady] = useState(false);
  const [mostrarGeocercas, setMostrarGeocercas] = useState(true);
  const [selectedGeocerca, setSelectedGeocerca] = useState<Geocerca | null>(null);
  const [showGeocercaPanel, setShowGeocercaPanel] = useState(false);
  const [showDispositivosPanel, setShowDispositivosPanel] = useState(false);
  
  const mapRef = useRef<google.maps.Map | null>(null);

  const cargarDatos = useCallback(async () => {
    if (!token) {
      setError('Token de autenticación no disponible');
      return;
    }

    try {
      console.log('Cargando datos del backend...');
      
      const [ubicacionesData, geocercasData, eventosData] = await Promise.all([
        getUbicacionesOnAsset(token),
        obtenerGeocercas(token),
        obtenerEventosGeocerca(token, { limite: 50 })
      ]);

      setUbicacionesOnAsset(ubicacionesData);
      setGeocercas(geocercasData);
      setEventos(eventosData);
      setError(null);
      setLoading(false);
      console.log(`Datos cargados: ${ubicacionesData.length} ubicaciones OnAsset, ${geocercasData.length} geocercas`);
      console.log('Ubicaciones OnAsset recibidas:', ubicacionesData.map(u => ({ 
        sentinel_id: u.sentinel_id, 
        friendly_name: u.friendly_name,
        latitude: u.latitude,
        longitude: u.longitude,
        timeOfReport: u.timeOfReport
      })));
    } catch (err: any) {
      console.error('Error cargando datos:', err);
      if (err.response?.status === 401) {
        setError('Sesión expirada. Por favor, inicia sesión nuevamente.');
      } else {
        setError('Error al cargar datos del servidor');
      }
      setLoading(false);
    }
  }, [token]);

  useEffect(() => {
    if (isLoaded && token) {
      cargarDatos();
    }
  }, [isLoaded, token, cargarDatos]);

  // Función para enfocar en una geocerca específica
  const focusOnGeocerca = (geocerca: Geocerca) => {
    if (!mapRef.current) return;

    console.log('🎯 Enfocando en geocerca:', geocerca.nombre);
    
    if (geocerca.tipo === 'circular') {
      const center = {
        lat: parseFloat(geocerca.centro_latitud?.toString() || '0'),
        lng: parseFloat(geocerca.centro_longitud?.toString() || '0')
      };
      
      // Calcular zoom basado en el radio
      const radius = parseFloat(geocerca.radio?.toString() || '500');
      let zoom = 15;
      if (radius > 2000) zoom = 12;
      else if (radius > 1000) zoom = 13;
      else if (radius > 500) zoom = 14;
      
      mapRef.current.panTo(center);
      mapRef.current.setZoom(zoom);
      
    } else if (geocerca.tipo === 'poligonal' && geocerca.coordenadas) {
      // Para polígonos, ajustar bounds
      const bounds = new google.maps.LatLngBounds();
      geocerca.coordenadas.forEach((coord: any) => {
        bounds.extend(new google.maps.LatLng(coord.lat, coord.lng));
      });
      mapRef.current.fitBounds(bounds);
    }
    
    setSelectedGeocerca(geocerca);
  };

  // Función para mostrar todas las geocercas
  const showAllGeocercas = () => {
    if (!mapRef.current || geocercas.length === 0) return;

    console.log('🌍 Mostrando todas las geocercas');
    
    const bounds = new google.maps.LatLngBounds();
    
    geocercas.forEach(geocerca => {
      if (geocerca.tipo === 'circular') {
        const center = new google.maps.LatLng(
          parseFloat(geocerca.centro_latitud?.toString() || '0'),
          parseFloat(geocerca.centro_longitud?.toString() || '0')
        );
        bounds.extend(center);
        
        // Extender bounds considerando el radio
        const radius = parseFloat(geocerca.radio?.toString() || '500');
        const radiusInDegrees = radius / 111320; // Aproximación
        
        bounds.extend(new google.maps.LatLng(
          center.lat() + radiusInDegrees,
          center.lng() + radiusInDegrees
        ));
        bounds.extend(new google.maps.LatLng(
          center.lat() - radiusInDegrees,
          center.lng() - radiusInDegrees
        ));
        
      } else if (geocerca.tipo === 'poligonal' && geocerca.coordenadas) {
        geocerca.coordenadas.forEach((coord: any) => {
          bounds.extend(new google.maps.LatLng(coord.lat, coord.lng));
        });
      }
    });
    
    mapRef.current.fitBounds(bounds);
    setSelectedGeocerca(null);
  };

  // Función para mostrar todos los dispositivos
  const showAllDispositivos = () => {
    if (!mapRef.current || ubicacionesOnAsset.length === 0) return;

    console.log('📱 Mostrando todas las ubicaciones OnAsset');
    
    const bounds = new google.maps.LatLngBounds();
    
    ubicacionesOnAsset.forEach(ubicacion => {
      if (ubicacion.latitude && ubicacion.longitude) {
        const position = new google.maps.LatLng(
          ubicacion.latitude,
          ubicacion.longitude
        );
        bounds.extend(position);
      }
    });

    if (!bounds.isEmpty()) {
      mapRef.current.fitBounds(bounds);
      const zoom = mapRef.current.getZoom();
      if (zoom && zoom > 15) {
        mapRef.current.setZoom(15);
      }
    }
  };

  // Función para obtener información de la geocerca
  const getGeocercaInfo = (geocerca: Geocerca) => {
    if (geocerca.tipo === 'circular') {
      return `Radio: ${geocerca.radio}m`;
    } else {
      return `Polígono con ${geocerca.coordenadas?.length || 0} vértices`;
    }
  };

  const onMapLoad = useCallback((map: google.maps.Map) => {
    mapRef.current = map;
    setIsMapReady(true);
    console.log('Mapa cargado y referencia establecida');
  }, []);

  const onMapUnmount = useCallback(() => {
    mapRef.current = null;
    setIsMapReady(false);
    console.log('Mapa desmontado');
  }, []);

  const renderDispositivos = () => {
    return ubicacionesOnAsset.map((ubicacion) => {
      // Determinar colores según el tiempo del último reporte
      const tiempoReporte = new Date(ubicacion.timeOfReport);
      const ahora = new Date();
      const horasDesdeReporte = (ahora.getTime() - tiempoReporte.getTime()) / (1000 * 60 * 60);
      
      // Verde si es reciente (menos de 2 horas), amarillo si es moderado (2-24 horas), rojo si es antiguo (más de 24 horas)
      let borderColor = '#EF4444'; // Rojo por defecto
      
      if (horasDesdeReporte < 2) {
        borderColor = '#10B981'; // Verde
      } else if (horasDesdeReporte < 24) {
        borderColor = '#F59E0B'; // Amarillo/Naranja
      }
      
      return (
        <Marker
          key={`onasset-${ubicacion.sentinel_id}`}
          position={{
            lat: ubicacion.latitude,
            lng: ubicacion.longitude
          }}
          title={`${ubicacion.friendly_name || ubicacion.sentinel_id} - ${ubicacion.marca} ${ubicacion.modelo} - Último reporte: ${ubicacion.tiempo_desde_reporte}`}
          icon={{
            url: 'data:image/svg+xml;charset=UTF-8,' + encodeURIComponent(`
              <svg width="50" height="50" viewBox="0 0 50 50" fill="none" xmlns="http://www.w3.org/2000/svg">
                <!-- Sombra del dispositivo -->
                <rect x="18.5" y="10.5" width="13" height="24" rx="2.5" ry="2.5" fill="rgba(0,0,0,0.2)"/>
                <!-- Cuerpo del celular -->
                <rect x="18" y="10" width="14" height="25" rx="3" ry="3" fill="white" stroke="${borderColor}" stroke-width="3.5"/>
                <!-- Pantalla -->
                <rect x="19.5" y="13.5" width="11" height="16" rx="1" ry="1" fill="#1F2937"/>
                <!-- Reflejo de la pantalla -->
                <rect x="19.5" y="13.5" width="5" height="16" rx="1" ry="1" fill="url(#screenGradient)" opacity="0.3"/>
                <!-- Botón home -->
                <circle cx="25" cy="32" r="1.5" fill="${borderColor}"/>
                <!-- Speaker -->
                <rect x="21.5" y="11.5" width="7" height="1" rx="0.5" ry="0.5" fill="#9CA3AF"/>
                <!-- Cámara frontal -->
                <circle cx="22.5" cy="12" r="0.5" fill="#6B7280"/>
                
                <!-- Gradiente para el reflejo -->
                <defs>
                  <linearGradient id="screenGradient" x1="0%" y1="0%" x2="100%" y2="0%">
                    <stop offset="0%" style="stop-color:white;stop-opacity:1" />
                    <stop offset="100%" style="stop-color:white;stop-opacity:0" />
                  </linearGradient>
                </defs>
              </svg>
            `),
            scaledSize: new google.maps.Size(50, 50),
            anchor: new google.maps.Point(25, 25)
          }}
          onClick={() => {
            // Mostrar información detallada del sentinel
            const infoContent = `
              <div style="max-width: 300px;">
                <h3 style="margin: 0 0 10px 0; color: #1f2937;">${ubicacion.friendly_name || ubicacion.sentinel_id}</h3>
                <div style="font-size: 14px; line-height: 1.4;">
                  <p><strong>Sentinel ID:</strong> ${ubicacion.sentinel_id}</p>
                  <p><strong>Dispositivo:</strong> ${ubicacion.marca} ${ubicacion.modelo}</p>
                  <p><strong>IMEI:</strong> ${ubicacion.imei}</p>
                  <p><strong>Ubicación:</strong> ${ubicacion.latitude.toFixed(6)}, ${ubicacion.longitude.toFixed(6)}</p>
                  <p><strong>Último reporte:</strong> ${ubicacion.tiempo_desde_reporte}</p>
                  ${ubicacion.temperatureC ? `<p><strong>Temperatura:</strong> ${ubicacion.temperatureC}°C</p>` : ''}
                  ${ubicacion.batteryPercent ? `<p><strong>Batería:</strong> ${ubicacion.batteryPercent}%</p>` : ''}
                  ${ubicacion.humidity ? `<p><strong>Humedad:</strong> ${ubicacion.humidity}%</p>` : ''}
                  <p><strong>Estado:</strong> <span style="color: ${borderColor};">●</span> ${horasDesdeReporte < 2 ? 'Reciente' : horasDesdeReporte < 24 ? 'Moderado' : 'Antiguo'}</p>
                </div>
              </div>
            `;
            
            const infoWindow = new google.maps.InfoWindow({
              content: infoContent
            });
            
            infoWindow.open(mapRef.current, this);
          }}
        />
      );
    });
  };

  const renderGeocercas = () => {
    if (!mostrarGeocercas) return null;

    return geocercas.map((geocerca) => {
      const color = geocerca.color || '#3B82F6';
      
      if (geocerca.tipo === 'circular') {
        const center = {
          lat: parseFloat(geocerca.centro_latitud?.toString() || '0'),
          lng: parseFloat(geocerca.centro_longitud?.toString() || '0')
        };
        const radius = parseFloat(geocerca.radio?.toString() || '500');

        return (
          <Circle
            key={`circle-${geocerca.id}`}
            center={center}
            radius={radius}
            options={{
              fillColor: color,
              fillOpacity: selectedGeocerca?.id === geocerca.id ? 0.4 : 0.2,
              strokeColor: color,
              strokeOpacity: selectedGeocerca?.id === geocerca.id ? 1 : 0.8,
              strokeWeight: selectedGeocerca?.id === geocerca.id ? 3 : 2,
              clickable: true
            }}
            onClick={() => setSelectedGeocerca(geocerca)}
          />
        );
      } else if (geocerca.tipo === 'poligonal' && geocerca.coordenadas) {
        const paths = geocerca.coordenadas.map((coord: any) => ({
          lat: coord.lat,
          lng: coord.lng
        }));

        return (
          <Polygon
            key={`polygon-${geocerca.id}`}
            paths={paths}
            options={{
              fillColor: color,
              fillOpacity: selectedGeocerca?.id === geocerca.id ? 0.4 : 0.2,
              strokeColor: color,
              strokeOpacity: selectedGeocerca?.id === geocerca.id ? 1 : 0.8,
              strokeWeight: selectedGeocerca?.id === geocerca.id ? 3 : 2,
              clickable: true
            }}
            onClick={() => setSelectedGeocerca(geocerca)}
          />
        );
      }
      return null;
    });
  };

  if (loadError) {
    return (
      <div className="flex items-center justify-center h-96 bg-red-50 border border-red-200 rounded-lg">
        <div className="text-center">
          <div className="text-red-600 text-lg font-semibold mb-2">Error al cargar Google Maps</div>
          <div className="text-red-500 text-sm">{loadError.message}</div>
        </div>
      </div>
    );
  }

  if (!isLoaded) {
    return (
      <div className="flex items-center justify-center h-96 bg-gray-50 border border-gray-200 rounded-lg">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 mx-auto mb-4"></div>
          <div className="text-gray-600">Cargando Google Maps...</div>
        </div>
      </div>
    );
  }

  return (
    <div className="h-full flex flex-col">
      {/* Header con controles */}
      <div className="bg-white border-b border-gray-200 p-4">
        <div className="flex justify-between items-center">
          <div className="flex items-center gap-4">
            <h2 className="text-xl font-semibold text-gray-800">
              Mapa de dispositivos
            </h2>
            {loading && (
              <div className="flex items-center gap-2 text-blue-600">
                <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-blue-600"></div>
                <span className="text-sm">Cargando...</span>
              </div>
            )}
            {error && (
              <div className="text-red-600 text-sm bg-red-50 px-3 py-1 rounded">
                {error}
              </div>
            )}
          </div>
          
          <div className="flex items-center gap-2">
            <label className="flex items-center gap-2 text-sm">
              <input
                type="checkbox"
                checked={mostrarGeocercas}
                onChange={(e) => setMostrarGeocercas(e.target.checked)}
                className="rounded"
              />
              Mostrar Geocercas
            </label>
            
            <button
              onClick={cargarDatos}
              className="bg-blue-500 hover:bg-blue-600 text-white px-4 py-2 rounded text-sm"
            >
              Actualizar
            </button>

            {/* Botón para mostrar/ocultar panel de geocercas */}
            <button
              onClick={() => setShowGeocercaPanel(!showGeocercaPanel)}
              className="bg-purple-500 hover:bg-purple-600 text-white px-4 py-2 rounded text-sm flex items-center gap-2"
            >
              📍 Geocercas ({geocercas.length})
            </button>

            {/* Botón para mostrar/ocultar panel de dispositivos */}
            <button
              onClick={() => setShowDispositivosPanel(!showDispositivosPanel)}
              className="bg-purple-500 hover:bg-purple-600 text-white px-4 py-2 rounded text-sm flex items-center gap-2"
            >
              📡 Tags ({ubicacionesOnAsset.length})
            </button>
          </div>
        </div>
      </div>

      {/* Mapa con panel de geocercas */}
      <div className="flex-1 relative">
        <GoogleMap
          mapContainerStyle={MAP_CONFIG.mapContainerStyle}
          center={MAP_CONFIG.center}
          zoom={MAP_CONFIG.zoom}
          onLoad={onMapLoad}
          onUnmount={onMapUnmount}
        >
          {renderDispositivos()}
          {renderGeocercas()}
        </GoogleMap>

        {/* Panel de controles de geocercas */}
        {showGeocercaPanel && (
          <div className="absolute top-4 left-4 z-10 bg-white rounded-lg shadow-lg p-4 max-w-sm">
            <div className="flex justify-between items-center mb-3">
              <h3 className="font-bold text-lg">Geocercas ({geocercas.length})</h3>
              <button
                onClick={() => setShowGeocercaPanel(false)}
                className="text-gray-500 hover:text-gray-700 text-xl"
              >
                ×
              </button>
            </div>
            
            {/* Botón para mostrar todas */}
            <button
              onClick={showAllGeocercas}
              className="w-full mb-3 px-4 py-2 bg-blue-500 text-white rounded hover:bg-blue-600 transition-colors"
            >
              📍 Ver todas las geocercas
            </button>
            
            {/* Lista de geocercas */}
            <div className="space-y-2 max-h-96 overflow-y-auto">
              {geocercas.map((geocerca) => (
                <div
                  key={geocerca.id}
                  className={`p-3 border rounded-lg cursor-pointer transition-all ${
                    selectedGeocerca?.id === geocerca.id
                      ? 'border-blue-500 bg-blue-50'
                      : 'border-gray-200 hover:border-gray-300 hover:bg-gray-50'
                  }`}
                  onClick={() => focusOnGeocerca(geocerca)}
                >
                  <div className="flex items-center space-x-2">
                    <div
                      className="w-4 h-4 rounded-full border-2"
                      style={{ backgroundColor: geocerca.color || '#3B82F6' }}
                    ></div>
                    <div className="flex-1">
                      <div className="font-medium text-sm">{geocerca.nombre}</div>
                      <div className="text-xs text-gray-500">
                        {geocerca.tipo === 'circular' ? '⭕' : '📐'} {getGeocercaInfo(geocerca)}
                      </div>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}

        {/* Panel de controles de dispositivos */}
        {showDispositivosPanel && (
          <div className="absolute top-4 right-4 z-10 bg-white rounded-lg shadow-lg p-4 max-w-sm">
            <div className="flex justify-between items-center mb-3">
              <h3 className="font-bold text-lg">Sentinels OnAsset ({ubicacionesOnAsset.length})</h3>
              <button
                onClick={() => setShowDispositivosPanel(false)}
                className="text-gray-500 hover:text-gray-700 text-xl"
              >
                ×
              </button>
            </div>
            
            {/* Botón para mostrar todos */}
            <button
              onClick={showAllDispositivos}
              className="w-full mb-3 px-4 py-2 bg-purple-500 text-white rounded hover:bg-purple-600 transition-colors"
            >
              📡 Ver todas las tags
            </button>
            
            {/* Lista de dispositivos */}
            <div className="space-y-2 max-h-96 overflow-y-auto">
              {ubicacionesOnAsset.map((ubicacion) => {
                const tiempoReporte = new Date(ubicacion.timeOfReport);
                const ahora = new Date();
                const horasDesdeReporte = (ahora.getTime() - tiempoReporte.getTime()) / (1000 * 60 * 60);
                
                let colorEstado = 'bg-red-500 border-red-600';
                let textoEstado = '🔴 Antiguo';
                
                if (horasDesdeReporte < 2) {
                  colorEstado = 'bg-green-500 border-green-600';
                  textoEstado = '🟢 Reciente';
                } else if (horasDesdeReporte < 24) {
                  colorEstado = 'bg-yellow-500 border-yellow-600';
                  textoEstado = '🟡 Moderado';
                }
                
                return (
                  <div
                    key={ubicacion.sentinel_id}
                    className="p-3 border rounded-lg cursor-pointer transition-all border-gray-200 hover:border-gray-300 hover:bg-gray-50"
                    onClick={() => {
                      if (mapRef.current) {
                        mapRef.current.setCenter({
                          lat: ubicacion.latitude,
                          lng: ubicacion.longitude
                        });
                        mapRef.current.setZoom(16);
                      }
                    }}
                  >
                    <div className="flex items-center space-x-2">
                      <div className={`w-4 h-4 rounded-full border-2 ${colorEstado}`}></div>
                      <div className="flex-1">
                        <div className="font-medium text-sm">
                          {ubicacion.friendly_name || ubicacion.sentinel_id}
                        </div>
                        <div className="text-xs text-gray-500">
                          {textoEstado} • {ubicacion.marca} {ubicacion.modelo}
                        </div>
                        <div className="text-xs text-gray-400">
                          📍 {ubicacion.latitude.toFixed(6)}, {ubicacion.longitude.toFixed(6)}
                        </div>
                        <div className="text-xs text-gray-400">
                          🕒 {ubicacion.tiempo_desde_reporte}
                        </div>
                        {ubicacion.temperatureC && (
                          <div className="text-xs text-gray-400">
                            🌡️ {ubicacion.temperatureC}°C
                          </div>
                        )}
                        {ubicacion.batteryPercent && (
                          <div className="text-xs text-gray-400">
                            🔋 {ubicacion.batteryPercent}%
                          </div>
                        )}
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>
          </div>
        )}

        {/* Panel de información de geocerca seleccionada */}
        {selectedGeocerca && (
          <div className="absolute bottom-4 left-4 bg-white rounded-lg shadow-lg p-4 max-w-sm z-10">
            <div className="flex justify-between items-start mb-2">
              <h4 className="font-bold text-lg">{selectedGeocerca.nombre}</h4>
              <button
                onClick={() => setSelectedGeocerca(null)}
                className="text-gray-500 hover:text-gray-700"
              >
                ×
              </button>
            </div>
            <p className="text-sm text-gray-600 mb-2">{selectedGeocerca.descripcion}</p>
            <div className="text-xs space-y-1">
              <div><strong>Tipo:</strong> {selectedGeocerca.tipo}</div>
              {selectedGeocerca.tipo === 'circular' && (
                <>
                  <div><strong>Radio:</strong> {selectedGeocerca.radio}m</div>
                  <div><strong>Centro:</strong> {selectedGeocerca.centro_latitud}, {selectedGeocerca.centro_longitud}</div>
                </>
              )}
              {selectedGeocerca.tipo === 'poligonal' && (
                <div><strong>Vértices:</strong> {selectedGeocerca.coordenadas?.length || 0}</div>
              )}
              <div><strong>Acción entrada:</strong> {selectedGeocerca.accion_entrada}</div>
              <div><strong>Acción salida:</strong> {selectedGeocerca.accion_salida}</div>
            </div>
          </div>
        )}

        {/* Panel de eventos */}
        {eventos.length > 0 && (
          <div className="absolute top-4 right-4 bg-white rounded-lg shadow-lg p-4 max-w-sm max-h-96 overflow-y-auto z-10">
            <h3 className="font-bold text-lg mb-2">Eventos Recientes</h3>
            <div className="space-y-2">
              {eventos.slice(0, 10).map((evento) => (
                <div key={evento.id} className="text-xs border-b border-gray-100 pb-2">
                  <div className="font-medium">{evento.dispositivo.nombre_dispositivo}</div>
                  <div className="text-gray-600">{evento.geocerca.nombre}</div>
                  <div className="text-gray-500">
                    {evento.tipo_evento} - {new Date(evento.timestamp_evento).toLocaleString()}
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}

      </div>
    </div>
  );
};

export default GoogleMapScreenWithGeocercaControls;
